import * as requestFromServer from './aiReportsCrud';
import {RequestCancellationManager} from '../../_common/utils/RequestCancellationManager';
import {v4 as guid} from 'uuid';
import {callTypes, aiReportsSlice} from "./aiReportsSlice";
import {callTypesSavedQueries, savedQueriesSlice} from "./savedQueriesSlice";

const {actions} = aiReportsSlice;
const {actions: savedQueriesActions} = savedQueriesSlice;

interface QueryParams {
  pageNumber?: number;
  pageSize?: number;
}

type Dispatch = (action: any) => void;

export const aiReportsPrefix = `getReportList-${guid()}`;
export const queriesListPrefix = `getQueriesList-${guid()}`;

const getReportList = (storeMethod: any) =>
  (queryParams: QueryParams) =>
    (dispatch: Dispatch) => {
      dispatch(actions.startCall({callType: callTypes.list}));
      const requestID = aiReportsPrefix;
      const cancellationHandlers = RequestCancellationManager.createCancellationHandlers(requestID);

      return requestFromServer
        .getReport(queryParams, cancellationHandlers)
        .then(response => {
          const {
            errorCode, data: {
              canAnswer,
              reason,
              columns,
              totalCount,
              list,
              pageCount,
              reportId
            }
          } = response.data;
          if (errorCode !== 0) throw new Error();

          dispatch(storeMethod({
            canAnswer,
            reason,
            totalCount,
            pageCount,
            columns,
            entities: list ?? [],
            reportId
          }));
        })
        .catch(error => {
          console.error({error});
          error.clientMessage = 'Can\'t find report';
          dispatch(actions.catchError({error, callType: callTypes.list}));
        })
        .finally(() => {
          RequestCancellationManager.forgetRequest(requestID);
        });
    };

export const getReport = getReportList(actions.aiReportsFetched);
export const getNextReport = getReportList(actions.aiReportsAppended);

export const getReportListForExcel = async (reportId: string) => {
  try {
    const response = await requestFromServer.getReportForExcel(reportId);
    const {errorCode, data: {columns, list}} = response.data;
    if (errorCode !== 0) throw new Error();
    return {
      columns,
      list
    };
  } catch (error: unknown) {
    console.error({error});
  }
};

export const saveQuery = async (reportId: string) => {
  try {
    const response = await requestFromServer.saveQuery(reportId);
    const {errorCode} = response.data;
    if (errorCode !== 0) throw new Error();
    return errorCode;
  } catch (error: unknown) {
    console.error({error});
  }
};

const getQueries = (storeMethod: any) =>
  (queryParams: QueryParams) =>
    (dispatch: Dispatch) => {
      dispatch(savedQueriesActions.startCall({callType: callTypesSavedQueries.list}));
      const requestID = queriesListPrefix;

      return requestFromServer
        .getQueriesList(queryParams)
        .then(response => {
          const {
            errorCode, data: {
              totalCount,
              list,
              pageCount,
            }
          } = response.data;
          if (errorCode !== 0) throw new Error();

          dispatch(storeMethod({
            totalCount,
            pageCount,
            entities: list ?? [],
          }));
        })
        .catch(error => {
          console.error({error});
          error.clientMessage = 'Can\'t find queries';
          dispatch(savedQueriesActions.catchError({error, callType: callTypesSavedQueries.list}));
        })
        .finally(() => {
          RequestCancellationManager.forgetRequest(requestID);
        });
    };

export const getQueriesList = getQueries(savedQueriesActions.savedQueriesFetched);
export const getNextQueriesList = getQueries(savedQueriesActions.savedQueriesAppended);

export const deleteQuery = async (reportId: string) => {
  try {
    const response = await requestFromServer.deleteQuery(reportId);
    const {errorCode} = response.data;
    if (errorCode !== 0) throw new Error();
    return errorCode;
  } catch (error: unknown) {
    console.error({error});
  }
};